import { Typography } from '@dewise-devex/backstage-plugin-mui-uikit-react';
import React from 'react';
import { useLocation } from 'react-router';

/**
 * Represents the structure of error details.
 */
export type ErrorMap = {
  /** Title of the error message. */
  title: string;
  /** Detailed message of the error. */
  message: string;
  /** Optional image representation for the error. */
  image?: {
    /** Unicode hex code of the primary emoji. */
    main: string;
    /** Unicode hex code of the secondary emoji (optional). */
    sub?: string;
  };
};

/**
 * Predefined error messages and their corresponding metadata.
 */
const errorMap: Record<string, ErrorMap> = {
  default: {
    title: 'Something went wrong',
    message: 'Please try again later.',
    image: {
      main: '1f625', // 😥
    },
  },
  user_not_found: {
    title: "Couldn't find any user",
    message: "Looks like the user isn't in our system.",
    image: {
      main: '1f914', // 🤔
      sub: '1f4ad', // 💭
    },
  },
};

/**
 * Component to render an emoji based on provided `ErrorMap.image` data.
 *
 * @param props - Image data containing the primary and optional secondary emoji.
 */
const Emoji = (props: ErrorMap['image']) => {
  if (!props) {
    return null;
  }

  if (!props.sub) {
    return (
      <span
        style={{
          fontSize: '53px',
          lineHeight: '53px',
          display: 'inline-block',
          marginBottom: '24px',
        }}
      >
        {String.fromCodePoint(parseInt(props.main, 16))}
      </span>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        fontSize: '53px',
        lineHeight: '53px',
        marginBottom: '24px',
      }}
    >
      <span>{String.fromCodePoint(parseInt(props.main, 16))}</span>
      <span
        style={{
          fontSize: '34px',
          lineHeight: '34px',
          marginTop: '-17px',
        }}
      >
        {String.fromCodePoint(parseInt(props.sub, 16))}
      </span>
    </div>
  );
};

/**
 * A generic error component that dynamically renders based on the current route.
 * Uses predefined error metadata to display an error message and optional emoji.
 *
 * @category ErrorHandling
 */
export const GenericError = () => {
  const location = useLocation();

  /**
   * Derives the error metadata based on the current route.
   */
  const error = React.useMemo(() => {
    const errorKey = location.pathname.includes('user') ? 'user_not_found' : '';
    return errorMap[errorKey] || errorMap.default;
  }, [location]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: 'calc(100vh - 250px)',
        marginTop: '100px',
      }}
    >
      {error.image && <Emoji {...error.image} />}
      <Typography
        variant="titleLG"
        sx={{ color: theme => theme.palette.surface.onSurface }}
      >
        {error.title}
      </Typography>
      <Typography
        variant="bodyLG"
        sx={{ color: theme => theme.palette.surface.onSurfaceVar }}
      >
        {error.message}
      </Typography>
    </div>
  );
};
